import React, { createContext, useState, useEffect, useContext } from "react";
import { useTranslation } from 'react-i18next';
/**
* Interface describing the shape of the Preferences context.
*/
interface PreferencesContextType {
    /** Currently selected language code (e.g., "cz") */
    selectedLanguage: string;
    /** Setter function to update the selected language */
    setSelectedLanguage: (lang: string) => void;
    /** Currently selected currency code (e.g., "czk") */
    selectedCurrency: string;
    /** Setter function to update the selected currency */
    setSelectedCurrency: (currency: string) => void;
}

/** React Context for user preferences */
const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

/**
* PreferencesProvider component that wraps children and provides language and currency.
*
* @param children - React children components to provide context to.
*/
export const PreferencesProvider= ({ children }: { children: React.ReactNode}) => {
    const { t, i18n } = useTranslation(); // Translation
    // Selected language
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        const saved = sessionStorage.getItem("selectedLanguage");
        return saved ? JSON.parse(saved) : "cz";
    });
    useEffect(() => {
        i18n.changeLanguage(selectedLanguage.toLowerCase())
        sessionStorage.setItem("selectedLanguage", JSON.stringify(selectedLanguage));
    }, [selectedLanguage]);

    // Selected currency
    const [selectedCurrency, setSelectedCurrency] = useState(() => {
        const saved = sessionStorage.getItem("selectedCurrency");
        return saved ? JSON.parse(saved) : "czk";
    });
    useEffect(() => {
        sessionStorage.setItem("selectedCurrency", JSON.stringify(selectedCurrency));
    }, [selectedCurrency]);

    return (
        <PreferencesContext.Provider value={{ selectedLanguage, setSelectedLanguage, selectedCurrency, setSelectedCurrency}}>
        {children}
        </PreferencesContext.Provider>
    );
}
/**
* Custom hook to access PreferencesContext.
* Throws error if used outside of a PreferencesProvider.
*
* @returns The current preferences context including language and currency states and setters.
*/
export const usePreferences= () => {
    const context = useContext(PreferencesContext);
    if (!context) throw new Error("usePreferences must be used within a PreferencesProvider");
    return context;
};